<template>
  <div class="manage-invoice mb-5 mx-3 mt-3">
    <h1 class="mb-3">{{ isCreateMode ? 'Create' : 'Edit' }} Invoice</h1>

    <div class="card">
      <div class="card-body">
        <!-- Creation mode fields -->
        <template v-if="isCreateMode">
          <div class="row mb-3">
            <div class="col-md-6 col-12">
              <label class="form-label">Client (Optional)</label>
              <accordion-select :options="clientOptionsFormatted" :splitText="false" v-model="form.client_id" @update:modelValue="clientChanged" />
            </div>
            <div class="col-md-6 col-12">
              <label class="form-label">Paid By <span class="text-danger">*</span></label>
              <accordion-select :options="paidByOptionsFormatted" :splitText="false" v-model="form.paid_by" @update:modelValue="paidByChanged" :class="{'is-invalid': errors.paid_by}" />
              <div v-if="errors.paid_by" class="invalid-feedback">{{ errors.paid_by }}</div>
            </div>
          </div>
          <div class="row mb-4">
            <div class="col-md-6 col-12">
              <label class="form-label">Invoice Date <span class="text-danger">*</span></label>
              <datepicker-utc v-model="form.date" :class="{'is-invalid': errors.date}" />
              <div v-if="errors.date" class="invalid-feedback">{{ errors.date }}</div>
            </div>
            <div class="col-md-6 col-12">
              <!-- Empty column for layout -->
            </div>
          </div>
        </template>
        <!-- Edit mode fields -->
        <template v-else>
          <div class="row mb-3">
            <div class="col-md-6 col-12">
              <label class="form-label">Client</label>
              <input type="text" class="form-control" :value="invoice.client?.name" disabled aria-label="Client">
            </div>
            <div class="col-md-6 col-12">
              <label class="form-label">Paid By</label>
              <input type="text" class="form-control" :value="invoice.paid_by?.name" disabled aria-label="Paid By">
            </div>
          </div>
          <div class="row mb-4">
            <div class="col-md-4 col-12">
              <label class="form-label">Invoice Date</label>
              <input type="text" class="form-control" :value="utc_date(invoice.date)" disabled aria-label="Invoice Date">
            </div>
          </div>
        </template>

        <!-- Line Items -->
        <div class="card table-card mb-3">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="mb-0">Line Items</h4>
            <button v-if="!isViewMode" class="btn btn-secondary" @click.prevent="addLineItem"><i class="fa fa-plus me-1"></i>Add Item</button>
          </div>
          <div class="card-body p-0">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th></th>
                    <th style="width:250px">Item</th>
                    <th style="width:190px">Discount</th>
                    <th>Description</th>
                    <th style="width:100px">Qty</th>
                    <th width="1">Total</th>
                    <th width="1">Net</th>
                    <th width="1">Tax</th>
                    <th></th>
                  </tr>
                </thead>
                <draggable tag="tbody" v-model="form.line_items" handle=".drag-handle" item-key="order" :disabled="isViewMode">
                  <template #item="{element, index}">
                    <tr class="align-middle">
                      <td class="drag-handle align-middle"><i class="fas fa-grip-vertical"></i></td>
                      <td>
                        <accordion-select :options="catalogOptionsFormatted" :splitText="false" v-model="element.stripe_catalog_id" @update:modelValue="catalogChanged(index)" :disabled="isViewMode" />
                      </td>
                      <td>
                        <accordion-select :options="couponOptionsFormatted" :splitText="false" v-model="element.stripe_coupon_id" @update:modelValue="couponChanged(index)" :disabled="isViewMode" />
                      </td>
                      <td>
                        <span v-if="element.description">
                          {{ truncateWords(element.description, 20) }}
                          <template v-if="!isViewMode">
                            <a href="#" @click.prevent="openDescriptionModal(index)">more</a>
                          </template>
                        </span>
                        <span v-else>
                          <button v-if="!isViewMode" class="btn btn-none btn-sm" @click="openDescriptionModal(index)"><img class="icon" src="/img/icons/avid-icons/edit.svg" alt="" /></button>
                        </span>
                      </td>
                      <td>
                        <input type="number" min="1" class="form-control" v-model.number="element.quantity" @input="recalc(index)" :disabled="isViewMode" aria-label="Number">
                      </td>
                      <td>{{ currency_with_zero(element.total_amount) }}</td>
                      <td>{{ currency_with_zero(element.net_amount) }}</td>
                      <td>{{ currency_with_zero(element.tax_amount || 0) }}</td>
                      <td>
                        <button v-if="!isViewMode" class="btn btn-none btn-sm" @click.prevent="removeLineItem(index)"><img class="icon red-icon" src="/img/icons/avid-icons/trash.svg" alt=""></button>
                      </td>
                    </tr>
                  </template>
                </draggable>
              </table>
          </div>
        </div>

        <h5>Total: {{ currency_with_zero(totalAmount) }}</h5>
        <h6 class="text-muted">Net: {{ currency_with_zero(netAmount) }}</h6>

        <div class="d-flex gap-2">
          <button v-if="!isViewMode" class="btn btn-primary" :disabled="saving" @click.prevent="save"><img class="icon me-1" src="/img/icons/avid-icons/save.svg" alt="">Save</button>
          <button class="btn btn-secondary" @click.prevent="back"><img class="icon me-1" src="/img/icons/avid-icons/close.svg" alt="">{{ isViewMode ? 'Back' : 'Cancel' }}</button>
          <button v-if="!isViewMode && canChargeInvoice" class="btn btn-warning" :disabled="charging || hasUnsavedChanges" @click.prevent="chargeInvoice" :title="hasUnsavedChanges ? 'Please save changes before charging' : ''">
            <img class="icon me-1" src="/img/icons/avid-icons/card-tick.svg" alt="">Charge Invoice
          </button>
        </div>
      </div>
    </div>

    <!-- Description Modal -->
    <div class="modal fade" id="descriptionModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header"><h5 class="modal-title">Edit Description</h5><button type="button" class="btn-close" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close"></button></div>
          <div class="modal-body"><textarea class="form-control" rows="4" v-model="modalDescription"></textarea></div>
          <div class="modal-footer"><button class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button><button class="btn btn-primary" @click="saveDescription" data-bs-dismiss="modal">Save</button></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import AccordionSelect from '../components/AccordionSelect.vue';
import DatepickerUtc from '../components/DatepickerUtc.vue';
import Swal from 'sweetalert2';
import debounce from 'lodash.debounce';
export default {
  name: 'ManageInvoice',
  components: { draggable, AccordionSelect, DatepickerUtc },
  props: {
    invoiceId: { type: Number, default: null },
    invoice: { type: Object, default: null },
    initialClients: { type: Array, default: () => [] },
    initialAgencies: { type: Array, default: () => [] }
  },
  data() {
    return {
      form: { 
        line_items: [],
        client_id: null,
        paid_by: null,
        date: null
      },
      originalLineItems: [],
      catalogOptions: [],
      couponOptions: [],
      clientOptions: [],
      agencyOptions: [],
      paidByOptions: [],
      saving: false,
      modalDescription: '',
      editingIndex: -1,
      taxLoading: false,
      billingContact: {},
      taxFlag: false,
      charging: false,
      isViewMode: false,
      errors: {},
    };
  },
  computed: {
    isCreateMode() {
      return !this.invoice && !this.invoiceId;
    },
    clientOptionsFormatted() {
      return this.clientOptions.map(c => ({ value: c.id, text: c.name }));
    },
    paidByOptionsFormatted() {
      let filtered = [];
      
      const clientId = this.form.client_id;
      
      if (clientId) {
        const id = typeof clientId === 'object' ? clientId.value : clientId;
        const sel = this.clientOptions.find(c => c.id === id);
        if (sel) {
          filtered.push({ value: sel.id, text: sel.name, type: 'App\\Models\\Client' });
          
          // Add all agencies
          this.agencyOptions.forEach(a => {
            filtered.push({ value: a.id, text: a.name, type: 'App\\Models\\Agency' });
          });
        }
      } else {
        filtered = this.paidByOptions.map(o => ({ value: o.id, text: o.name, type: o.type || 'App\\Models\\Client' }));
      }
      return filtered;
    },
    catalogOptionsFormatted() {
      return this.catalogOptions.map(c => ({ 
        value: c.price_id, 
        text: `${c.name} - ${this.currency_with_zero(c.unit_amount/100)}`, 
        category: c.name, 
        price: c.unit_amount/100, 
        frequency: c.recurring 
      }));
    },
    couponOptionsFormatted() {
      // filter coupons like ManageProposal component
      const coupons = this.couponOptionsFiltered;
      return coupons.map(c => ({ value: c.id, text: c.name }));
    },
    couponOptionsFiltered() {
      let paidById, paidByType;
      if (this.isCreateMode) {
        const paidBy = this.form.paid_by;
        paidById = paidBy && typeof paidBy === 'object' ? paidBy.value : paidBy?.id;
        paidByType = paidBy && typeof paidBy === 'object' ? paidBy.type : paidBy?.type;
      } else {
        paidById = this.invoice?.paid_by?.id;
        paidByType = this.invoice?.paid_by_type;
      }
      return this.couponOptions.filter(c=>{
        if(!c.id) return false;
        if(!c.id.startsWith('agency_')) return true;
        if(paidByType==='App\\Models\\Agency' && `agency_${paidById}`===c.id) return true;
        return false;
      });
    },
    totalAmount() {
      return this.form.line_items.reduce((a,li)=>a+Number(li.total_amount||0),0);
    },
    netAmount() {
      return this.form.line_items.reduce((a,li)=>a+Number(li.net_amount||0),0);
    },
    taxAmountTotal(){
      return this.form.line_items.reduce((a,li)=>a+Number(li.tax_amount||0),0);
    },
    canChargeInvoice() {
      if (this.isCreateMode) return false;
      if (!this.invoice) return false;
      if (this.invoice.status === 'paid') return false;
      const invoiceDate = new Date(this.invoice.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      invoiceDate.setHours(0, 0, 0, 0);
      return invoiceDate <= today;
    },
    hasUnsavedChanges() {
      if (this.isCreateMode) return false;
      if (!this.originalLineItems || !this.form.line_items) return false;
      if (this.originalLineItems.length !== this.form.line_items.length) return true;
      
      // Deep compare line items
      const original = JSON.stringify(this.originalLineItems.map(li => ({
        stripe_catalog_id: typeof li.stripe_catalog_id === 'object' ? li.stripe_catalog_id?.value : li.stripe_catalog_id,
        stripe_coupon_id: typeof li.stripe_coupon_id === 'object' ? li.stripe_coupon_id?.value : li.stripe_coupon_id,
        description: li.description || null,
        name: li.name || null,
        quantity: parseInt(li.quantity),
        total_amount: parseFloat(li.total_amount),
        net_amount: parseFloat(li.net_amount),
        tax_amount: parseFloat(li.tax_amount || 0),
        order: parseInt(li.order || 0)
      })));
      
      const current = JSON.stringify(this.form.line_items.map(li => ({
        stripe_catalog_id: typeof li.stripe_catalog_id === 'object' ? li.stripe_catalog_id?.value : li.stripe_catalog_id,
        stripe_coupon_id: typeof li.stripe_coupon_id === 'object' ? li.stripe_coupon_id?.value : li.stripe_coupon_id,
        description: li.description || null,
        name: li.name || null,
        quantity: parseInt(li.quantity),
        total_amount: parseFloat(li.total_amount),
        net_amount: parseFloat(li.net_amount),
        tax_amount: parseFloat(li.tax_amount || 0),
        order: parseInt(li.order || 0)
      })));
      
      return original !== current;
    }
  },
  async created() {
    const params = new URLSearchParams(window.location.search);
    this.isViewMode = params.get('view') === '1' || params.get('mode') === 'view';
    
    if (this.isCreateMode) {
      // Initialize for creation mode
      this.clientOptions = this.initialClients || [];
      this.agencyOptions = this.initialAgencies || [];
      this.paidByOptions = [...this.clientOptions, ...this.agencyOptions];
      // Set default date to today
      this.form.date = new Date().toISOString().split('T')[0];
      // Add initial line item
      this.addLineItem();
    } else {
      this.initializeData();
    }
    
    await this.loadBillingDropdowns();
  },
  methods: {
    initializeData() {
      if (this.invoice) {
        this.form.line_items = JSON.parse(JSON.stringify(this.invoice.line_items));
        // Store original line items for comparison
        this.originalLineItems = JSON.parse(JSON.stringify(this.invoice.line_items));
        this.billingContact = this.invoice.paid_by?.billing_contact || {address:'',city:'',region:'',postal_code:'',country:''};
        this.taxFlag = this.invoice.paid_by?.tax_exempt || this.invoice.paid_by?.resale_certificate || false;
      }
    },
    clientChanged() {
      if (!this.form.client_id) {
        return;
      }
      const id = typeof this.form.client_id === 'object' ? this.form.client_id.value : this.form.client_id;
      const sel = this.clientOptions.find(c => c.id === id);
      if (sel) {
        // Auto-set paid_by to the selected client if not already set to an agency
        const currentPaidByType = this.form.paid_by && typeof this.form.paid_by === 'object' ? this.form.paid_by.type : null;
        if (currentPaidByType !== 'App\\Models\\Agency') {
          this.form.paid_by = { value: sel.id, text: sel.name, type: 'App\\Models\\Client' };
        }
      }
    },
    paidByChanged() {
      const paidById = typeof this.form.paid_by === 'object' ? this.form.paid_by?.value : this.form.paid_by;
      const paidByType = typeof this.form.paid_by === 'object' ? this.form.paid_by?.type : null;
      
      if (!paidById || !paidByType) {
        this.billingContact = { address: '', city: '', region: '', postal_code: '', country: 'US' };
        this.taxFlag = false;
        return;
      }
      
      // Find the entity (client or agency)
      let entity = null;
      if (paidByType === 'App\\Models\\Client') {
        entity = this.clientOptions.find(c => c.id === paidById);
      } else if (paidByType === 'App\\Models\\Agency') {
        entity = this.agencyOptions.find(a => a.id === paidById);
      }
      
      if (entity) {
        this.billingContact = entity.billing_contact || { address: '', city: '', region: '', postal_code: '', country: 'US' };
        this.taxFlag = paidByType === 'App\\Models\\Client' ? !!entity.tax_exempt : !!entity.resale_certificate;
        // Trigger tax recalculation
        this.queueTaxEstimate();
      }
    },
    async loadBillingDropdowns() {
      const [catalogRes, couponRes] = await Promise.all([
        window.axios.get('/api/billing/stripe/catalog'),
        window.axios.get('/api/billing/stripe/coupons')
      ]);
      this.catalogOptions = catalogRes.data.data;
      this.couponOptions = couponRes.data.data;

      // After catalog is loaded, update stripe_product_id for any existing line items that have a matching catalog entry
      this.form.line_items.forEach((li, idx) => {
        const catId = typeof li.stripe_catalog_id === 'object' ? li.stripe_catalog_id?.value : li.stripe_catalog_id;
        if (!catId) return;
        const cat = this.getCatalog(catId);
        if (cat) {
          li.unit_amount = cat.unit_amount / 100;
          li.name = cat.name;
          li.stripe_product_id = cat.product_id;
          // recalc totals since unit amount changed
          this.recalc(idx);
        }
      });
    },
    getCatalog(pid){ return this.catalogOptions.find(c=>c.price_id===pid); },
    getCoupon(cid){ return this.couponOptions.find(c=>c.id===cid); },
    catalogChanged(i){ 
      const li=this.form.line_items[i]; 
      const cat=this.getCatalog(li.stripe_catalog_id?.value||li.stripe_catalog_id); 
      li.unit_amount=cat?cat.unit_amount/100:0; 
      li.stripe_product_id=cat?cat.product_id:null; 
      li.name=cat?cat.name:null;
      // Auto-populate description if it's empty and we have a catalog item
      if(cat && !li.description) {
        li.description = cat.description;
      }
      this.recalc(i); 
    },
    couponChanged(i){ this.recalc(i); },
    recalc(i){ 
      const li=this.form.line_items[i]; 
      li.total_amount=li.unit_amount*li.quantity; 
      let net=li.total_amount; 
      const couponId= li.stripe_coupon_id ? (typeof li.stripe_coupon_id==='object'?li.stripe_coupon_id.value:li.stripe_coupon_id):null;
      const coupon=couponId?this.getCoupon(couponId):null;
      if(coupon){ 
        if(coupon.percent_off) net=net*(1-coupon.percent_off/100); 
        else if(coupon.amount_off) net=net-coupon.amount_off/100; 
      } 
      li.net_amount=Math.max(0,net); 
      this.queueTaxEstimate(); 
    },
    truncateWords(text, num){ if(!text) return ''; const words=text.split(/\s+/); if(words.length<=num) return text; return words.slice(0,num).join(' ')+'…'; },
    addLineItem(){ if(this.isViewMode) return; this.form.line_items.push({ stripe_catalog_id:null,stripe_coupon_id:null,description:'',quantity:1,unit_amount:0,total_amount:0,net_amount:0,order:this.form.line_items.length,stripe_product_id:null,tax_amount:0}); this.queueTaxEstimate(); },
    removeLineItem(i){ if(this.isViewMode) return; this.form.line_items.splice(i,1); this.queueTaxEstimate(); },
    openDescriptionModal(i){ if(this.isViewMode) return; this.editingIndex=i; this.modalDescription=this.form.line_items[i].description||''; new bootstrap.Modal(document.getElementById('descriptionModal')).show(); },
    saveDescription(){ if(this.isViewMode) return; if(this.editingIndex>=0){ this.form.line_items[this.editingIndex].description=this.modalDescription; this.editingIndex=-1; } },
    async save(){ 
      if(this.isViewMode) return;
      
      // Validate required fields in creation mode
      if (this.isCreateMode) {
        this.errors = {};
        const paidById = typeof this.form.paid_by === 'object' ? this.form.paid_by?.value : this.form.paid_by;
        let paidByType = typeof this.form.paid_by === 'object' ? this.form.paid_by?.type : null;
        if (paidById && !paidByType) {
          const opt = this.paidByOptionsFormatted.find(o => o.value === paidById || o.value === Number(paidById));
          paidByType = opt?.type || null;
        }
        if (!paidById || !paidByType) {
          this.errors.paid_by = 'Paid By is required';
        }
        if (!this.form.date) {
          this.errors.date = 'Invoice Date is required';
        }
        if (this.form.line_items.length === 0) {
          await Swal.fire({
            title: 'Error',
            text: 'Please add at least one line item.',
            icon: 'error',
            confirmButtonText: 'OK',
            customClass: {
              popup: 'swal-error'
            }
          });
          return;
        }
        
        if (Object.keys(this.errors).length > 0) {
          return;
        }
      }
      
      this.saving=true; 
      
      if (this.isCreateMode) {
        // Create new invoice
        const paidById = typeof this.form.paid_by === 'object' ? this.form.paid_by.value : this.form.paid_by;
        let paidByType = typeof this.form.paid_by === 'object' ? this.form.paid_by.type : null;
        // Resolve type from options if missing (e.g. when AccordionSelect drops it)
        if (paidById && !paidByType) {
          const opt = this.paidByOptionsFormatted.find(o => o.value === paidById || o.value === Number(paidById));
          paidByType = opt?.type || null;
        }
        const clientId = typeof this.form.client_id === 'object' ? this.form.client_id?.value : this.form.client_id;
        
        const payload = {
          client_id: clientId || null,
          paid_by_id: paidById,
          paid_by_type: paidByType,
          date: this.form.date,
          line_items: this.form.line_items.map(li => ({
            ...li, 
            stripe_catalog_id: li.stripe_catalog_id?.value || li.stripe_catalog_id, 
            stripe_coupon_id: li.stripe_coupon_id?.value || li.stripe_coupon_id,
            name: li.name || null,
            description: li.description || null,
            quantity: li.quantity,
            total_amount: li.total_amount,
            net_amount: li.net_amount,
            tax_amount: li.tax_amount || 0,
            order: li.order || 0
          }))
        };
        
        try {
          const { data } = await window.axios.post('/api/billing/invoices', payload);
          await Swal.fire({
            title: 'Invoice Created!',
            text: 'The invoice has been created successfully.',
            icon: 'success',
            customClass: {
              popup: 'swal-success'
            }
          });
          window.location.href = '/admin/invoices';
        } catch(e) {
          console.error(e);
          const errorMessage = e.response?.data?.message || 'Failed to create invoice. Please try again.';
          await Swal.fire({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK',
            customClass: {
              popup: 'swal-error'
            }
          });
        } finally {
          this.saving = false;
        }
      } else {
        // Update existing invoice
        const payload = { 
          line_items: this.form.line_items.map(li => ({
            ...li, 
            stripe_catalog_id: li.stripe_catalog_id?.value || li.stripe_catalog_id, 
            stripe_coupon_id: li.stripe_coupon_id?.value || li.stripe_coupon_id,
            name: li.name || null,
            description: li.description || null
          }))
        }; 
        try { 
          await window.axios.put(`/api/billing/invoices/${this.invoiceId}`, payload); 
          // Update original line items after successful save
          this.originalLineItems = JSON.parse(JSON.stringify(this.form.line_items));
          await Swal.fire({
            title: 'Invoice Updated!',
            text: 'The invoice has been saved successfully.',
            icon: 'success',
            customClass: {
              popup: 'swal-success'
            }
          });
          //Go back to /admin/invoices
          window.location.href = '/admin/invoices';
        } catch(e) {
          console.error(e); 
          await Swal.fire({
            title: 'Error',
            text: 'Failed to save invoice. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK',
            customClass: {
              popup: 'swal-error'
            }
          });
        } 
        finally { 
          this.saving = false; 
        }
      }
    },
    queueTaxEstimate: debounce(function(){ this.fetchTaxEstimate(); }, 500),
    async fetchTaxEstimate(){
      if(!this.form.line_items.length) return;
      
      let paidById, paidByType;
      if (this.isCreateMode) {
        const paidBy = this.form.paid_by;
        paidById = paidBy && typeof paidBy === 'object' ? paidBy.value : paidBy?.id;
        paidByType = paidBy && typeof paidBy === 'object' ? paidBy.type : paidBy?.type;
      } else {
        paidById = this.invoice?.paid_by?.id;
        paidByType = this.invoice?.paid_by_type;
      }
      
      if(!paidById || !paidByType) return;
      this.taxLoading = true;
      const payload = {
        paid_by_id: paidById,
        paid_by_type: paidByType,
        billing_contact: this.billingContact,
        tax_exempt: this.taxFlag,
        line_items: this.form.line_items
          .filter(li => !!li.stripe_product_id)
          .map(li => {
            const catalogId = typeof li.stripe_catalog_id === 'object' ? li.stripe_catalog_id?.value : li.stripe_catalog_id;
            const qty = Number(li.quantity || 1);
            return {
              stripe_catalog_id: catalogId,
              stripe_product_id: li.stripe_product_id,
              unit_amount: li.unit_amount,
              quantity: qty
            };
          })
      };
      try{
        const res = await window.axios.post('/api/billing/tax-estimate', payload);
        const updated = res.data.data.line_items;
        updated.forEach((u,idx)=>{
          this.form.line_items[idx].tax_amount = u.tax_amount;
        });
      }catch(e){
        console.error('Tax calculation failed:', e);
      }finally{
        this.taxLoading = false;
      }
    },
    async chargeInvoice() {
      if (this.isViewMode) {
        return;
      }
      if (!this.canChargeInvoice) {
        return;
      }
      
      const result = await Swal.fire({
        title: 'Charge Invoice?',
        text: 'Are you sure you want to charge this invoice?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, charge it',
        cancelButtonText: 'Cancel',
        customClass: {
          popup: 'swal-warning'
        }
      });

      if (!result.isConfirmed) {
        return;
      }

      this.charging = true;
      try {
        await window.axios.post(`/api/billing/invoices/${this.invoiceId}/charge`);
        await Swal.fire({
          title: 'Invoice Charged!',
          text: 'The invoice has been charged successfully.',
          icon: 'success',
          customClass: {
            popup: 'swal-success'
          }
        });
        window.location.href = '/admin/invoices';
      } catch (e) {
        console.error(e);
        const errorMessage = e.response?.data?.message || 'Failed to charge invoice. Please try again.';
        await Swal.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK',
          customClass: {
            popup: 'swal-error'
          }
        });
      } finally {
        this.charging = false;
      }
    },
    back(){ window.history.back(); }
  }
};
</script>
